import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { blogPosts } from '../data/mock';
import { Card, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowRight, Clock, ExternalLink, Newspaper, RefreshCw, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InsightsPage() {
  const [liveNews, setLiveNews] = useState([]);
  const [isLoadingNews, setIsLoadingNews] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');

  const fetchLatestNews = async () => {
    setIsLoadingNews(true);
    setNewsError(null);
    
    try {
      // Fetch news with updated query for latest trade news including AU-EU deals
      const response = await fetch(`${API_URL}/api/news/trade?query=Australia%20trade%20EU%20import%20export%20deals%202026&limit=12`);
      const data = await response.json();
      
      if (data.status === 'success' && data.articles.length > 0) {
        // Remove duplicate articles based on title
        const uniqueArticles = data.articles.reduce((acc, article) => {
          const titleKey = article.title.toLowerCase().trim();
          if (!acc.some(a => a.title.toLowerCase().trim() === titleKey)) {
            acc.push(article);
          }
          return acc;
        }, []).slice(0, 8); // Limit to 8 unique articles
        
        setLiveNews(uniqueArticles);
        toast.success(`Loaded ${uniqueArticles.length} latest trade news articles`);
      } else {
        setNewsError('No news articles found. Please try again later.');
        toast.error('No news articles found');
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
      setNewsError('Failed to fetch news. Please try again.');
      toast.error('Failed to fetch news');
    } finally {
      setIsLoadingNews(false);
    }
  };

  // Clean HTML tags from description
  const cleanDescription = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').substring(0, 200) + '...';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredPosts = activeFilter === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeFilter);

  return (
    <div className="min-h-screen">
      <SEO
        title="News & Insights"
        path="/insights"
        description="Latest trade news, market insights, and industry analysis for Australian importers and exporters."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Knowledge Hub
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                <span className="text-white">News & </span><span className="text-teal-400">Insights</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Stay updated with the latest trade news, expert insights on international trade, 
                import and export procedures, compliance, and global market trends.
              </p>
            </div>
          </div>
        </section>

        {/* Live News Section */}
        <section className="py-12 bg-gradient-to-br from-teal-50 to-emerald-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <div className="mb-4 md:mb-0">
                <div className="flex items-center gap-3 mb-2">
                  <Newspaper className="h-6 w-6 text-teal-600" />
                  <h2 className="text-2xl font-bold text-navy-900">Live Trade News</h2>
                </div>
                <p className="text-slate-600">
                  Get the latest trade-related news from around the world
                </p>
              </div>
              <Button
                onClick={fetchLatestNews}
                disabled={isLoadingNews}
                data-testid="fetch-news-btn"
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              >
                {isLoadingNews ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Latest News
                  </span>
                )}
              </Button>
            </div>

            {/* News Error */}
            {newsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                {newsError}
              </div>
            )}

            {/* Live News Grid */}
            {liveNews.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="live-news-grid">
                {liveNews.map((article, index) => (
                  <a
                    key={index}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                    data-testid={`news-article-${index}`}
                  >
                    <Card className="h-full bg-white border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300 p-5">
                      <div className="flex items-start gap-2 mb-3">
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700 text-xs">
                          Live
                        </Badge>
                        <span className="text-xs text-slate-500 truncate">
                          {article.source}
                        </span>
                      </div>
                      <CardTitle className="text-sm font-semibold text-navy-900 mb-2 line-clamp-3 group-hover:text-teal-700 transition-colors">
                        {article.title}
                      </CardTitle>
                      {article.published_at && (
                        <p className="text-xs text-slate-500 mt-auto">
                          {formatDate(article.published_at)}
                        </p>
                      )}
                      <div className="flex items-center gap-1 mt-3 text-xs font-medium text-teal-600">
                        Read More
                        <ExternalLink className="h-3 w-3" />
                      </div>
                    </Card>
                  </a>
                ))}
              </div>
            )}

            {/* Empty State */}
            {liveNews.length === 0 && !isLoadingNews && !newsError && (
              <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300">
                <Newspaper className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-600 mb-2">No live news loaded</h3>
                <p className="text-slate-500 mb-4">Click the button above to fetch the latest trade news</p>
              </div>
            )}
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {['All', 'Trade Guide', 'Market Insights', 'Industry Trends', 'Compliance'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeFilter === cat
                      ? 'bg-navy-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-4">Our Insights</h2>
              <p className="text-lg text-slate-600">
                Expert articles and guides on international trade
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {filteredPosts.map((post, index) => (
                <Card
                  key={post.id}
                  className={`group overflow-hidden border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300 ${
                    index === 0 && activeFilter === 'All' ? 'md:col-span-2' : ''
                  }`}
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className={`${index === 0 && activeFilter === 'All' ? 'md:grid md:grid-cols-2' : ''}`}>
                    {/* Image */}
                    <div className={`h-48 ${index === 0 && activeFilter === 'All' ? 'md:h-full md:min-h-[280px]' : ''} overflow-hidden`}>
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                          {post.category}
                        </Badge>
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Clock className="h-4 w-4" />
                          {post.readTime}
                        </div>
                      </div>
                      <CardTitle className="text-xl text-navy-900 mb-3 group-hover:text-teal-700 transition-colors">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="text-slate-600 mb-4">
                        {post.excerpt}
                      </CardDescription>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">
                          {new Date(post.date).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                        <a 
                          href={post.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm font-medium text-navy-900 hover:text-teal-600 transition-colors"
                        >
                          Read More
                          <ExternalLink className="ml-1 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* View All Blog CTA */}
            <div className="text-center mt-12">
              <Link to="/blog">
                <Button className="bg-navy-900 hover:bg-navy-800 text-white px-8 py-4 rounded-xl">
                  View All Articles
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">
              Stay Informed
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Subscribe to our newsletter for the latest trade insights and industry updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                data-testid="newsletter-email-input"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <Button 
                className="bg-navy-900 hover:bg-navy-800 text-white px-6 py-3 rounded-xl"
                data-testid="newsletter-subscribe-btn"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
